/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { ReactNode } from 'react';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { CTAType, useAvroTracking } from '../../../observability';
import {
  CustomBrandButton,
  CustomBrandButtonProps,
} from '../../CustomBrandButton/CustomBrandButton';

export type CustomCTACenteredProps = {
  heading: string;
  button?: {
    variant?: CustomBrandButtonProps['variant'];
    text?: string;
    url?: string;
  };
  backgroundColor?: 'zapier' | 'earth' | 'moss' | 'night';
  children?: ReactNode;
};

const Styles = labelStyles('CustomCTAFilled', {
  root: (backgroundColor: CustomCTACenteredProps['backgroundColor']) =>
    css({
      padding: '96px 20px',
      backgroundColor: Colors[backgroundColor ?? 'earth'],

      [MEDIA_MIN_WIDTH.large]: {
        padding: '120px 20px',
      },
    }),

  content: css({
    maxWidth: '544px',
    margin: '0 auto',
    textAlign: 'center',

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '832px',
    },
  }),

  heading: css({
    fontFamily: 'GT Alpina, serif',
    fontSize: '36px',
    fontWeight: 250,
    lineHeight: 1.1,
    letterSpacing: '-1px',
    color: Colors.neutral100,

    [MEDIA_MIN_WIDTH.small]: {
      fontSize: '50px',
      letterSpacing: '-1.5px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      fontSize: '74px',
      letterSpacing: '-2px',
    },
  }),

  button: css({
    marginTop: '26px',

    [MEDIA_MIN_WIDTH.large]: {
      marginTop: '50px',
    },
  }),
});

export function CustomCTACentered(props: CustomCTACenteredProps) {
  const { emitCTAClickEvent } = useAvroTracking();

  return (
    <div css={Styles.root(props.backgroundColor)}>
      <div css={Styles.content}>
        <div>{props.children}</div>
        <div>
          <h2 css={Styles.heading}>{props.heading}</h2>
          {props.button?.text && props.button?.url && (
            <div css={Styles.button}>
              <CustomBrandButton
                href={props.button.url}
                onClick={() => {
                  emitCTAClickEvent({
                    text: props.button?.text ?? '',
                    href: props.button?.url ?? '',
                    section: 'CustomCTACentered',
                    type: CTAType.cta_button,
                  });
                }}
              >
                {props.button.text}
              </CustomBrandButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
